import React, { useState } from "react";
import axios from "axios";
import { Mail, Send, MapPin, MessageSquare, Youtube, Instagram, Facebook } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { SITE, SOCIALS } from "../config";

const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 1.6 1.1 3 2.4 3.9 1 .7 2.2 1.1 3.4 1.1v3.3c-1.7 0-3.4-.5-4.9-1.4v6.5a6.4 6.4 0 1 1-6.4-6.4c.4 0 .8 0 1.2.1v3.4a3 3 0 1 0 2 2.8V3h2.3z"/>
  </svg>
);
const socialIcon = (key, cls="w-4 h-4") => {
  switch(key){case"youtube":return <Youtube className={cls}/>;case"tiktok":return <TikTokIcon className={cls}/>;case"instagram":return <Instagram className={cls}/>;case"facebook":return <Facebook className={cls}/>;default:return null;}
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", topic: "General", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", topic: "General", message: "" });
      setTimeout(() => setSent(false), 6000);
    } catch (_) {
      setError("Message could not be sent. Please email us directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 pt-10">
      <div className="text-[11px] mono uppercase tracking-widest text-cyan-400">Contact Us</div>
      <h1 className="display-italic text-4xl sm:text-6xl mt-2 uppercase sd-title-gradient">Say hi, tip us off, or partner up.</h1>
      <p className="text-slate-400 mt-4 max-w-2xl">Weather scoop? Business inquiry? Fan mail? Drop us a note below and we’ll get back to you.</p>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-10">
        <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs mono uppercase tracking-widest text-slate-500">Your name</Label>
              <Input data-testid="contact-name-input" required value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))} className="bg-black/40 border-white/10 mt-1 h-11" />
            </div>
            <div>
              <Label className="text-xs mono uppercase tracking-widest text-slate-500">Email</Label>
              <Input data-testid="contact-email-input" required type="email" value={form.email} onChange={(e)=>setForm(f=>({...f,email:e.target.value}))} className="bg-black/40 border-white/10 mt-1 h-11" />
            </div>
          </div>
          <div>
            <Label className="text-xs mono uppercase tracking-widest text-slate-500">Topic</Label>
            <Select value={form.topic} onValueChange={(value)=>setForm(f=>({...f,topic:value}))}>
              <SelectTrigger data-testid="contact-topic-select" className="mt-1 h-11 bg-black/40 border-white/10 text-slate-100">
                <SelectValue placeholder="Select a topic" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#070b15] text-slate-100">
                {["General","Weather Tip / Storm Report","Partnership / Sponsorship","Media / Press","Technical Issue"].map((topic)=>(
                  <SelectItem key={topic} value={topic} data-testid={`contact-topic-${topic.toLowerCase().replace(/[^a-z]+/g,"-").replace(/^-|-$/g,"")}`}>{topic}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mono uppercase tracking-widest text-slate-500">Message</Label>
            <Textarea data-testid="contact-message-input" required rows={6} value={form.message} onChange={(e)=>setForm(f=>({...f,message:e.target.value}))}
              placeholder="How can we help?" className="bg-black/40 border-white/10 mt-1" />
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[11px] mono text-slate-500">We usually reply within 24–48 hours.</div>
            <Button type="submit" disabled={busy} data-testid="contact-submit-button" className="bg-red-600 hover:bg-red-500 text-white h-11 px-5 gap-2"><Send className="w-4 h-4" /> {busy ? "Sending…" : "Send message"}</Button>
          </div>
          {sent && <div data-testid="contact-success-message" className="text-emerald-400 text-sm mono">Thanks — your message has been received. We’ll be in touch.</div>}
          {error && <div data-testid="contact-error-message" className="text-red-400 text-sm mono">{error}</div>}
        </form>

        <aside className="space-y-4">
          <div className="glass rounded-2xl p-6">
            <div className="text-[11px] mono uppercase tracking-widest text-slate-500">Email</div>
            <a href={`mailto:${SITE.contactEmail}`} className="flex items-center gap-2 mt-1 text-slate-100 hover:text-cyan-300">
              <Mail className="w-4 h-4" /> {SITE.contactEmail}
            </a>
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="text-[11px] mono uppercase tracking-widest text-slate-500">Newsroom</div>
            <div className="flex items-center gap-2 mt-1 text-slate-200"><MapPin className="w-4 h-4 text-red-400" /> Tornado Alley, USA</div>
            <div className="flex items-center gap-2 mt-1 text-slate-400 text-sm"><MessageSquare className="w-4 h-4" /> Storm tips 24/7</div>
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="text-[11px] mono uppercase tracking-widest text-slate-500 mb-3">Follow the chase</div>
            <div className="grid grid-cols-2 gap-2">
              {SOCIALS.map(s => (
                <a key={s.key} href={s.href} target="_blank" rel="noreferrer noopener"
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.03] border border-white/5 hover:bg-white/[0.08] text-sm">
                  {socialIcon(s.key)} <span>{s.name}</span>
                </a>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Contact;
